import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getOrder } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, Shield, Package, ArrowRight, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { formatInr, MONEY_CLASSNAME } from '@/lib/money';

export default function OrderSuccessPage() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user || !orderId) { setLoading(false); return; }
    getOrder(orderId)
      .then(r => setOrder(r.data))
      .catch(() => toast.error('Could not load order'))
      .finally(() => setLoading(false));
  }, [user, orderId]);

  const copyOrderId = () => {
    navigator.clipboard.writeText(orderId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-lg mx-auto text-center py-32 px-5">
          <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold mb-2">Order not found</h2>
          <Link to="/orders"><Button variant="outline" className="rounded-full mt-4">View All Orders</Button></Link>
        </div>
      </div>
    );
  }

  const paymentConfirmed = order.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-background" data-testid="order-success-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-2xl mx-auto px-5 md:px-8 py-12">
        {/* Success Animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          className="flex justify-center mb-8"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/15 flex items-center justify-center" data-testid="success-icon">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-center tracking-tight mb-2" data-testid="success-title">
            {paymentConfirmed ? 'Payment Successful' : 'Payment Confirmation Pending'}
          </h1>
          <p className="text-muted-foreground text-center mb-10">
            {paymentConfirmed ? 'Your order is confirmed' : 'We are waiting for secure payment confirmation'}
          </p>
        </motion.div>

        {/* Order Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-card border border-border/40 rounded-2xl overflow-hidden mb-6"
          data-testid="success-order-card"
        >
          {/* Product */}
          <div className="flex items-center gap-4 p-5">
            <img src={order.image_url} alt={order.collectible_name} className="w-20 h-20 rounded-xl object-cover bg-secondary/30" />
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-lg truncate">{order.collectible_name}</h3>
              <p className="text-sm text-muted-foreground mt-0.5 capitalize">{order.metal_type} &middot; {order.weight}g &middot; {order.purity}</p>
              <p className="text-xs text-muted-foreground mt-1 font-mono">Serial: {order.serial_number}</p>
            </div>
          </div>

          <Separator className="bg-border/20" />

          {/* Order Details */}
          <div className="p-5 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order ID</span>
              <button onClick={copyOrderId} className="flex items-center gap-1.5 font-mono text-foreground hover:text-gold transition-colors" data-testid="copy-order-id">
                {orderId} {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{paymentConfirmed ? 'Amount Paid' : 'Order Total'}</span>
              <span className={`${MONEY_CLASSNAME} font-semibold text-gold`}>{formatInr(order.purchase_price)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payment Status</span>
              <span className={paymentConfirmed ? 'text-green-400 font-medium' : 'text-amber-400 font-medium'} data-testid="order-payment-status">{paymentConfirmed ? 'Confirmed' : 'Pending confirmation'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order Date</span>
              <span>{new Date(order.created_at).toLocaleDateString('en-IN', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>

            {order.price_breakdown && (
              <>
                <Separator className="bg-border/20 !my-4" />
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Price Breakdown</p>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between text-muted-foreground"><span>Supplier Cost</span><span className={MONEY_CLASSNAME}>{formatInr(order.price_breakdown.supplier_cost)}</span></div>
                  {order.price_breakdown.price_confirmed && [
                    ['Packaging', order.price_breakdown.packaging_cost],
                    ['Freight and insurance', order.price_breakdown.shipping_cost],
                    ['Allocated overhead', order.price_breakdown.allocated_overhead_cost],
                    ['Payment cost', order.price_breakdown.gateway_cost],
                  ].map(([label, amount]) => (
                    <div key={label} className="flex justify-between text-muted-foreground"><span>{label}</span><span className={MONEY_CLASSNAME}>{formatInr(amount)}</span></div>
                  ))}
                  <div className="flex justify-between text-muted-foreground"><span>{order.price_breakdown.profit_13_percent !== undefined ? '13% cost markup' : `SŌHM Margin (${order.price_breakdown.margin_percent || 13}%)`}</span><span className={MONEY_CLASSNAME}>{formatInr(order.price_breakdown.projected_profit ?? order.price_breakdown.profit_13_percent ?? order.price_breakdown.sohm_margin ?? order.price_breakdown.platform_margin)}</span></div>
                  <div className="flex justify-between text-muted-foreground"><span>GST ({order.price_breakdown.gst_percent || 3}%)</span><span className={MONEY_CLASSNAME}>{formatInr(order.price_breakdown.gst)}</span></div>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-3"
        >
          {paymentConfirmed && order.certificate_id && (
            <Link to={`/certificate/${order.certificate_id}`} className="block" data-testid="view-certificate-btn">
              <Button className="w-full rounded-full h-12 text-sm font-semibold bg-gold text-background hover:bg-gold/90">
                <Shield className="w-4 h-4 mr-2" /> View Digital Certificate
              </Button>
            </Link>
          )}
          <Link to="/orders" className="block" data-testid="view-orders-btn">
            <Button variant="outline" className="w-full rounded-full h-12 text-sm font-semibold">
              <Package className="w-4 h-4 mr-2" /> Track Order
            </Button>
          </Link>
          <Link to="/" className="block">
            <Button variant="ghost" className="w-full rounded-full h-12 text-sm text-muted-foreground hover:text-foreground">
              Continue Shopping <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>

        {/* Shipping Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 p-4 bg-secondary/30 rounded-xl border border-border/20 text-center"
          data-testid="shipping-note"
        >
          <p className="text-xs text-muted-foreground">
            You'll receive tracking details once your confirmed order ships. Track your order anytime from the Orders page.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
